/* eslint-disable array-element-newline */
/* eslint-disable max-lines-per-function */
import {extractElement} from 'extensions/extract-element';
import 'extensions/ui-components/x-tooltip';
import {defineCustomElement, pluralize} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import stylesheet from './x-product-inventory-message.scss?as=stylesheet';

/**
 * @type {string[]}
 */
const customerInventoryData = JSON.parse(extractElement('#customer-inventory-data') || '{}');

/**
 * @type {number}
 */
const customerDeliveryDays = parseInt(extractElement('#customer-delivery-days') || 0, 10);

/**
 * @type {import('../types').FujitsuRegionHierarchy[]}
 */
const regionHierarchyData = JSON.parse(extractElement('#region-hierarchy-data', true) || '{}');

/**
 * @type {import('../types').FujitsuRegionHierarchy}
 */
const customerRegion = regionHierarchyData?.find(regionHierarchy => customerInventoryData?.includes(regionHierarchy.Region));

/**
 * Enum for stock statuses.
 * @readonly
 * @enum {number}
 */
const Statuses = {
	Backorder: 0,
	In: 1,
	Low: 2,
	Out: 3
};

export class ProductInventoryMessageComponent extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			inventoryData: {
				attribute: 'inventory-data',
				type: Object
			},
			message: {
				state: true,
				type: String
			},
			status: {
				state: true,
				type: String
			}
		};
	}

	constructor () {
		super();

		/**
		 * @type {import('../types').FujitsuFinishedGoodsInventory}
		 */
		this.inventoryData = {};

		/**
		 * @type {string}
		 */
		this.message = '';

		/**
		 * @type {string}
		 */
		this.status = '';
	}

	/**
	 * @param {Statuses} status
	 * @param {number} [days]
	 */
	#setMessage (status, days = 0) {
		switch (status) {
			case Statuses.In:
				return html`In Stock - Expected Delivery in <x-tooltip placement="bottom-end"><span slot="content">Cutoff before 12pm</span><span class="underline">${days} Business ${pluralize('Day', days > 1)}*</span></x-tooltip>`;
			case Statuses.Low:
				return 'Low Stock';
			case Statuses.Backorder:
				return 'Backorder';
			case Statuses.Out:
				return 'Out of Stock';
			default:
				return '';
		}
	}

	/**
	 * @param {Statuses} status
	 */
	#setStatus (status) {
		switch (status) {
			case Statuses.In:
				return 'in';
			case Statuses.Low:
				return 'low';
			case Statuses.Backorder:
				return 'backorder';
			case Statuses.Out:
				return 'out';
			default:
				return '';
		}
	}

	/**
	 * @param {Statuses} status
	 * @param {number} [days]
	 */
	setStatus (status, days) {
		this.status = this.#setStatus(status);
		this.message = this.#setMessage(status, days);
	}

	determineStockStatus () {

		/**
		 * @type {import('../types').FujitsuSourceRegionXFujitsuFinishedGoodsInventoryRegion[]}
		 */
		const regionsWithInventory = customerRegion
			?.SourceRegions
			?.map(sourceRegion => {
				const region = this.inventoryData?.Regions?.find(productRegion => productRegion.Warehouse === sourceRegion.SourceRegion);

				return region
					? {
						...sourceRegion,
						...region
					}
					: false;
			})
			?.filter(regionWithInventory => regionWithInventory)
			?.sort((a, b) => a.Sequence - b.Sequence);

		if (regionsWithInventory?.length > 0) {
			// Determine if out of stock
			const outOfStock = regionsWithInventory.every(regionWithInventory => regionWithInventory.NoStock);
			if (outOfStock) {
				this.setStatus(Statuses.Out);

				return true;
			}

			// Get total current stock and low level amounts from all regions
			const [totalCurrentStock, totalLowStockAmount] = this.inventoryData?.Regions?.reduce(([_currentStock, _lowStockAmount], productRegion) => {
				return [
					_currentStock + productRegion.CurrentStock,
					_lowStockAmount + productRegion.LowStockAmount
				];
			}, [0, 0]) || [0, 0];

			// If total inventory is less than or equal to 0, set status to Backorder
			if (totalCurrentStock <= 0) {
				this.setStatus(Statuses.Backorder);

				return true;
			}

			// Show low stock if total stock is less than total low amount
			if (totalCurrentStock < totalLowStockAmount) {
				this.setStatus(Statuses.Low);

				return true;
			}

			// Attempt to find closest region with positive stock
			for (let currentRegion of regionsWithInventory) {
				if (currentRegion.CurrentStock > 0 && currentRegion.CurrentStock > currentRegion.LowStockAmount) {
					const days = currentRegion.AdditionalDays + customerDeliveryDays;

					this.setStatus(Statuses.In, days);

					return true;
				}
			}

			this.setStatus(Statuses.Backorder);

			return true;
		}

		this.status = '';
		this.message = '';

		return false;
	}

	async updated (changedProperties) {
		if (changedProperties.has('inventoryData')) {
			await this.updateComplete;

			this.determineStockStatus();
		}
	}

	renderMessage () {
		if (this.message && this.status) {
			const classes = {
				'inventory-message-icon': true,
				[`inventory-message-icon--${this.status}`]: true
			};

			return html`
				<span class="${classMap(classes)}" aria-hidden="true"></span>
				<span class="inventory-message-text">${this.message}</span>
			`;
		}

		return nothing;
	}

	render () {
		return html`<div class="inventory-message">
			${this.renderMessage()}
		</div>`;
	}
};

defineCustomElement('x-product-inventory-message', ProductInventoryMessageComponent);